<template>
  <div class="loans-table tw-mt-12">
    <div class="table-header">
      <div class="header-left">
        <h2 class="table-title">Loans</h2>
        <span class="borrowers-info">{{ subtitle }}</span>
      </div>
    </div>

    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Borrower</th>
            <th>Loan Amount</th>
            <th>Duration</th>
            <th>Interest</th>
            <th>Repayment Amount</th>
            <th>Repayment Date</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="sortedLoans.length === 0">
            <td colspan="7" class="tw-text-center tw-text-gray-500 tw-py-6">
              There are no loans to display.
            </td>
          </tr>
          <tr v-for="loan in sortedLoans" :key="loan._id" class="tw-text-sm">
            <td class="main-cell tw-font-semibold">
              {{ borrowerName(loan) }}
            </td>
            <td class="main-cell">
              {{ formatAmount(loan.amount, loan.currency) }}
            </td>
            <td class="sub-cell">{{ loan.duration }}</td>
            <td class="sub-cell">{{ formatInterest(loan.interest_rate) }}</td>
            <td class="sub-cell">
              {{ formatAmount(loan.repayment_amount, loan.currency) }}
            </td>
            <td class="sub-cell">{{ formatDate(loan.repayment_date) }}</td>
            <td class="sub-cell">
              <span class="status-badge" :class="statusClass(loan.status)">
                {{ formatStatus(loan.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="showPagination && totalPages > 1" class="tw-flex tw-justify-end tw-mt-4">
        <Pagination
          :totalPage="totalPages"
          :currentPage="currentPage"
          :maxVisible="5"
          @pageChange="$emit('go-to-page', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/navigation/Pagination.vue";

export default {
  name: "CreditLoansTable",
  components: {
    Pagination,
  },
  props: {
    loans: {
      type: Array,
      default: () => [],
    },
    subtitle: {
      type: String,
      default: "",
    },
    showPagination: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
  },
  emits: ["go-to-page"],
  computed: {
    // Show active loans first, preserving the backend order within each group.
    sortedLoans() {
      return [...this.loans].sort((a, b) => {
        const aActive = (a.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
        const bActive = (b.status || "").toUpperCase() === "ACTIVE" ? 0 : 1;
        return aActive - bActive;
      });
    },
  },
  methods: {
    // The creditor loans endpoint populates `borrower` down to the identity
    // allowlist: _id, fname, lname, username, email, image. Not every user has
    // a first/last name set, so fall back through the other names we are given
    // rather than showing a dash for a borrower we can perfectly well identify.
    borrowerName(loan) {
      const borrower = loan.borrower || loan.user;
      if (!borrower) return "—";
      // An unpopulated ref arrives as a bare id string, which names no one.
      if (typeof borrower === "string") return "—";
      const fullName = `${borrower.fname || ""} ${borrower.lname || ""}`.trim();
      return fullName || borrower.username || borrower.email || "—";
    },
    // A loan carries its own currency; NGN was hardcoded here, so a USD or KES
    // loan was rendered with a naira sign against a naira-sized number.
    formatAmount(amount, currency) {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: (currency || "NGN").toUpperCase(),
        minimumFractionDigits: 2,
      }).format(Number(amount) || 0);
    },
    formatInterest(rate) {
      return `${((Number(rate) || 0) * 100).toFixed(0)}%`;
    },
    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatStatus(status) {
      if (!status) return "—";
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },
    statusClass(status) {
      switch ((status || "").toUpperCase()) {
        case "REPAID":
        case "PAID":
        case "COMPLETED":
          return "status--success";
        case "OVERDUE":
        case "DEFAULTED":
          return "status--danger";
        case "PENDING":
          return "status--pending";
        default:
          return "status--active";
      }
    },
  },
};
</script>

<style scoped>
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.table-wrapper {
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e4e4e7;
  padding: 0px 24px 10px 24px;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

table th,
table td {
  padding: 20px;
  text-align: left;
  white-space: nowrap;
}

table th {
  color: #14151a;
  font-weight: 400;
}

table thead {
  font-weight: medium;
  border-bottom: 1px solid #e2e8f0;
}

.main-cell {
  color: #001140;
}

.sub-cell {
  color: #4f5877;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.table-title {
  font-size: 18px;
  font-weight: bold;
}

.borrowers-info {
  color: #4c5b90;
  font-size: 14px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status--active {
  background: #e5edff;
  color: #2a62f5;
}

.status--success {
  background: #e7f6ec;
  color: #1f9254;
}

.status--danger {
  background: #fbeaea;
  color: #db4343;
}

.status--pending {
  background: #fef3e0;
  color: #b25e09;
}
</style>
